import { useState } from 'react'

import { useRemoveFromLibrary } from '@/api/hooks'
import type { LibraryItem } from '@/api/types'
import { Button, Dialog, toast } from '@/components/ui'

import { errorMessage } from './errorMessage'

export interface RemoveTrackDialogProps {
  item: LibraryItem
  onClose: () => void
}

/**
 * Mounted only while there is a track to confirm, so the checkbox and any error
 * reset themselves between uses without an effect.
 */
export function RemoveTrackDialog({ item, onClose }: RemoveTrackDialogProps) {
  const [keepFile, setKeepFile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remove = useRemoveFromLibrary()

  function confirm() {
    setError(null)
    remove.mutate(
      { id: item.id, deleteFile: !keepFile },
      {
        onSuccess: () => {
          toast.success('Removed from library', item.track.title)
          onClose()
        },
        onError: (cause) => setError(errorMessage(cause)),
      },
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !remove.isPending) onClose()
      }}
      size="sm"
      title="Remove from library"
      description={`“${item.track.title}” by ${item.track.artist_name} will no longer appear in your library.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirm} loading={remove.isPending}>
            Remove
          </Button>
        </>
      }
    >
      <label className="flex min-h-11 items-center gap-3">
        <input
          type="checkbox"
          checked={keepFile}
          onChange={(event) => setKeepFile(event.target.checked)}
          className="h-4 w-4 shrink-0 accent-accent"
        />
        <span className="text-sm font-medium text-text">Keep the file on disk</span>
      </label>

      <p className="mt-1 text-sm text-text-muted">
        {keepFile
          ? 'The audio file stays in your library folder on the server. Only the entry in Tunes is removed.'
          : 'The audio file will be deleted from the server and the space freed.'}
      </p>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </Dialog>
  )
}
